import React, { Suspense, lazy } from "react";
import BImage from "../BImage.jsx";
import WaveformPlayer from "./WaveformPlayer.jsx";
import { isViewableModel } from "../../utils/fileFacts.js";
import "../../css/FilePreview.css";

// The only two consumers of three.js / @react-three in the app. Loading them
// eagerly puts the whole WebGL stack in the main bundle for every visitor, even
// though most sessions never open an .exr or a 3D model. Split behind React.lazy
// so those bytes are fetched on first use instead of on first paint.
const EXRViewer = lazy(() => import("../EXRViewer.jsx"));
const ModelViewer = lazy(() => import("../ModelViewer.jsx"));

const ViewerFallback = (
    <div className="unknown-file-type">
        <span>Loading viewer…</span>
    </div>
);

/**
 * Shared content preview — the single renderer used by both the File Details
 * modal (remote, signed-URL source) and the bulk importer (local object-URL
 * source). It is byte-source-agnostic: hand it a `url` (signed download URL or
 * an `URL.createObjectURL(file)`), a `name`, and a `mimetype`, and it picks the
 * right viewer.
 *
 *   audio → WaveformPlayer (play/scrub/loop/speed)
 *   image → BImage (click-to-fullscreen)
 *   video → <video controls>
 *   .exr  → EXRViewer       3D models → ModelViewer (see isViewableModel)
 *   pdf   → <embed>         text → <pre> (caller supplies `textContent`)
 *
 * @param {object}   props
 * @param {string}   [props.url]              playable/viewable source URL
 * @param {string}   [props.name]             filename (drives extension fallbacks)
 * @param {string}   [props.mimetype]         MIME type (drives the primary switch)
 * @param {string}   [props.textContent]      preloaded text body (text/* only)
 * @param {number[]} [props.peaks]            precomputed audio waveform peaks (audio only) — lets
 *                                            the player stream playback instead of downloading +
 *                                            decoding the whole file (see WaveformPlayer)
 * @param {number}   [props.peaksDuration]    media length in seconds that pairs with `peaks`
 * @param {boolean}  [props.waitingForPeaks]  peaks are still being fetched for a large audio file
 * @param {Function} [props.onCaptureThumbnail] 3D viewer → capture a thumbnail
 * @param {boolean}  [props.unavailable]      source could not be resolved (404/etc.)
 * @param {string}   [props.unavailableMessage] message shown when `unavailable`
 * @param {boolean}  [props.compact]          small/embedded host (e.g. importer): skip the
 *                                            heavy fixed-size 3D viewer and show a hint instead
 * @param {string}   [props.className]
 */
const FilePreview = ({
    url,
    name = "",
    mimetype = "",
    textContent,
    peaks,
    peaksDuration = 0,
    waitingForPeaks = false,
    onCaptureThumbnail,
    unavailable = false,
    unavailableMessage,
    compact = false,
    className = "",
}) => {
    const lowerName = name.toLowerCase();
    const type = mimetype ? mimetype.split("/")[0] : "unknown";

    let body;
    if (unavailable) {
        body = (
            <div className="unknown-file-type">
                <span>
                    {unavailableMessage ||
                        "This file’s content couldn’t be loaded right now. Try refreshing — if it persists, the file may have been removed."}
                </span>
            </div>
        );
    } else if (!url) {
        // Don't mount media players before the source resolves — a null src makes
        // them log spurious decode errors.
        body = (
            <div className="unknown-file-type">
                <span>Loading preview…</span>
            </div>
        );
    } else if (mimetype === "image/x-exr" || lowerName.endsWith(".exr")) {
        body = (
            <Suspense fallback={ViewerFallback}>
                <EXRViewer src={url} alt={name} />
            </Suspense>
        );
    } else if (isViewableModel(name)) {
        // The 3D viewer is a heavy, fixed-600px WebGL canvas — too tall (and too
        // costly to spin up per selection) for a small embedded host. Show a hint
        // there; render the full interactive viewer everywhere else.
        body = compact ? (
            <div className="unknown-file-type">
                <span>🧊 3D model — open it after import to view and rotate in 3D.</span>
            </div>
        ) : (
            <Suspense fallback={ViewerFallback}>
                <ModelViewer src={url} fileName={name} onCaptureThumbnail={onCaptureThumbnail} />
            </Suspense>
        );
    } else {
        switch (type) {
            case "image":
                body = <BImage src={url} alt={name} downloadName={name} />;
                break;
            case "audio":
                body = (
                    <WaveformPlayer
                        src={url}
                        className="media-player"
                        peaks={peaks}
                        peaksDuration={peaksDuration}
                        waitingForPeaks={waitingForPeaks}
                    />
                );
                break;
            case "video":
                body = (
                    <video controls className="media-player">
                        <source src={url} />
                    </video>
                );
                break;
            case "text":
                // `null` means the body is still being read (importer's async
                // local read). An empty string is a genuinely empty/unreadable
                // file — show the (empty) box, not a stuck loading state. Hosts
                // that pass text synchronously (file.content) never hit `null`.
                body =
                    textContent === null ? (
                        <div className="unknown-file-type">
                            <span>Loading preview…</span>
                        </div>
                    ) : (
                        <div className="text-preview">
                            <pre>{textContent}</pre>
                        </div>
                    );
                break;
            case "application":
                if (mimetype === "application/pdf" || lowerName.endsWith(".pdf")) {
                    body = <embed src={url} type={mimetype} className="pdf-preview" />;
                }
                break;
            default:
                break;
        }
        if (!body) {
            body = (
                <div className="unknown-file-type">
                    <span>No preview available for this file type.</span>
                </div>
            );
        }
    }

    return <div className={`qd-file-preview ${className}`.trim()}>{body}</div>;
};

export default FilePreview;
